import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './HowItWorksSection.module.css';
import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';

// Pulls the raw markdown/text string out of a Field-shaped data object or string
const getRawContent = field => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.content || field.markdown || field.text || '';
};

// Extracts string content from title field safely
const getTitleContent = titleField => {
  if (!titleField) return '';
  if (typeof titleField === 'string') return titleField;
  return titleField.content || titleField.text || '';
};

// Default fallback step rows matching the approved website copy
const DEFAULT_STEPS = [
  {
    blockId: 'track-step-1',
    title: { content: 'A starting picture' },
    text: {
      content:
        "Before the first lesson you answer a few short questions about your child's Arabic. That gives us a provisional starting level for each of the five skills.",
    },
  },
  {
    blockId: 'track-step-2',
    title: { content: 'The trial confirms it' },
    text: {
      content:
        'In the free trial lesson, the tutor assesses your child properly and confirms or corrects that starting point. You receive it in writing: where your child is on each skill, and what the tutor would work on first.',
    },
  },
  {
    blockId: 'track-step-3',
    title: { content: 'Every lesson is recorded' },
    text: {
      content:
        'After each lesson the tutor completes a short report — what was covered, which objectives were achieved, and a note for you.',
    },
  },
  {
    blockId: 'track-step-4',
    title: { content: 'Progress bars fill up' },
    text: {
      content:
        'Each of the five skills has its own progress bar showing how far through the current level your child is. You can see it any time.',
    },
  },
  {
    blockId: 'track-step-5',
    title: { content: 'A teacher confirms the level-up' },
    text: {
      content:
        "At around 80% of a level's objectives, the skill becomes eligible. The tutor confirms it. Never the software.",
    },
  },
  {
    blockId: 'track-step-6',
    title: { content: 'A certificate' },
    text: {
      content:
        'When your child completes a level, we issue a certificate naming the level and the skill. Yours to keep, print, and send to the people who will be proudest of it.',
    },
  },
];

const DEFAULT_TITLE = 'How it works, from the first lesson';

const HowItWorksSection = ({ blocks = [], options, title, description, sectionId }) => {
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const [expanded, setExpanded] = useState(0); // first item open by default on mobile

  const hasTitle = hasDataInFields([title], fieldOptions) || Boolean(title);
  const hasDescription = hasDataInFields([description], fieldOptions);

  // Every block whose blockId starts with "track-step-" becomes a numbered row, in Console order
  let stepBlocks = blocks.filter(block => block?.blockId?.startsWith('track-step-'));

  // Fallback: If no blocks start with "track-step-", check for "step-" or other blocks
  if (stepBlocks.length === 0) {
    stepBlocks = blocks.filter(block => block?.blockId?.startsWith('step-'));
  }
  if (stepBlocks.length === 0 && blocks.length > 0) {
    stepBlocks = blocks.filter(block => block?.title || block?.text);
  }
  if (stepBlocks.length === 0) {
    stepBlocks = DEFAULT_STEPS;
  }

  const toggle = index => {
    setExpanded(prev => (prev === index ? -1 : index));
  };

  return (
    <section id={sectionId || 'how-it-works-section-track'} className={css.howItWorksSection}>
      {/* ---------- Centered Section Title ---------- */}
      <div className={css.titleWrapper}>
        {hasTitle ? (
          <Field data={title} className={css.title} options={fieldOptions} />
        ) : (
          <h2 className={css.title}>{DEFAULT_TITLE}</h2>
        )}

        {hasDescription ? (
          <div className={css.descriptionWrapper}>
            <Field data={description} className={css.description} options={fieldOptions} />
          </div>
        ) : null}
      </div>

      {/* ---------- Desktop Table ---------- */}
      <div className={css.tableWrapper}>
        <div className={css.tableHeader}>
          <span className={css.colNumber} aria-label="Step number" />
          <span className={css.colStep}>Steps</span>
          <span className={css.colHappens}>What happens</span>
        </div>

        {stepBlocks.map((block, index) => {
          const titleText = getTitleContent(block?.title);
          const rawText = getRawContent(block?.text);
          const stepNumber = index + 1;
          const isAltRow = index % 2 === 1;

          return (
            <div
              key={block.blockId || `track-step-${stepNumber}`}
              className={`${css.tableRow} ${isAltRow ? css.tableRowAlt : ''}`}
            >
              <div className={css.colNumber}>
                <span className={css.stepNumber}>{stepNumber}</span>
              </div>
              <div className={css.colStep}>
                <span className={css.stepTitle}>
                  {titleText || (block?.title ? <Field data={block.title} options={fieldOptions} /> : null)}
                </span>
              </div>
              <div className={css.colHappens}>
                <div className={css.cellTextHappens}>
                  {rawText ? <p>{rawText}</p> : block?.text ? <Field data={block.text} options={fieldOptions} /> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ---------- Mobile Accordion ---------- */}
      <div className={css.accordionWrapper}>
        <div className={css.accordionContainer}>
          {stepBlocks.map((block, index) => {
            const titleText = getTitleContent(block?.title);
            const rawText = getRawContent(block?.text);
            const stepNumber = index + 1;
            const isOpen = expanded === index;

            return (
              <div
                key={block.blockId || `acc-step-${stepNumber}`}
                className={`${css.accordionItem} ${isOpen ? css.accordionItemOpen : ''}`}
              >
                <button
                  type="button"
                  className={css.accordionHeader}
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                >
                  <span className={css.accordionLabel}>
                    <span className={css.accordionStepBadge}>{stepNumber}</span>
                    <span className={css.accordionTitleText}>
                      {titleText || (block?.title ? <Field data={block.title} options={fieldOptions} /> : null)}
                    </span>
                  </span>
                  <svg
                    className={`${css.chevron} ${isOpen ? css.chevronOpen : ''}`}
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 8 10 12 14 8" />
                  </svg>
                </button>

                {isOpen ? (
                  <div className={css.accordionBody}>
                    <div className={css.accordionFieldGroup}>
                      <p className={css.accordionFieldLabel}>What happens</p>
                      <div className={css.accordionFieldValue}>
                        {rawText ? <p>{rawText}</p> : block?.text ? <Field data={block.text} options={fieldOptions} /> : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

HowItWorksSection.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  options: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  sectionId: PropTypes.string,
};

export default HowItWorksSection;
